/**
 * Utilities for cross-company property duplication UI.
 */

/** @type {ReadonlyArray<'queued'|'running'|'completed'|'partial'|'failed'|'cancelled'>} */
export const ACTIVE_STATUSES = ['queued', 'running']

/** @type {ReadonlyArray<'completed'|'partial'|'failed'|'cancelled'|'rolled_back'>} */
export const TERMINAL_STATUSES = ['completed', 'partial', 'failed', 'cancelled', 'rolled_back']

export const DEFAULT_DUPLICATION_OPTIONS = {
	tags: true,
	files: true,
	teams: true,
	comments: false,
	landlords: true,
	custom_fields: true,
	contacts: false,
	deals: false,
	activities: false,
}

export const OPTION_LABELS = {
	tags: 'Tags',
	files: 'Files',
	teams: 'Equipos',
	comments: 'Comentarios',
	landlords: 'Landlords',
	custom_fields: 'Custom fields',
	contacts: 'Contactos',
	deals: 'Negocios',
	activities: 'Actividades',
}

/** Opciones de propiedad (siempre visibles). */
export const PROPERTY_OPTION_KEYS = [
	'tags',
	'files',
	'teams',
	'comments',
	'landlords',
	'custom_fields',
]

/** Opciones CRM opcionales (requieren usuario origen). */
export const CRM_OPTION_KEYS = ['contacts', 'deals', 'activities']

export const ENTITY_COUNTER_KEYS = [
	'parents',
	'units',
	'photos',
	'files',
	'landlords',
	'custom_fields',
	'tags',
	'teams',
	'comments',
	'contacts',
	'deals',
	'activities',
]

export const ENTITY_COUNTER_LABELS = {
	parents: 'Padres',
	units: 'Unidades',
	photos: 'Fotos',
	files: 'Archivos',
	landlords: 'Landlords',
	custom_fields: 'Custom fields',
	tags: 'Tags',
	teams: 'Equipos',
	comments: 'Comentarios',
	contacts: 'Contactos',
	deals: 'Negocios',
	activities: 'Actividades',
	deals_won_excluded: 'Negocios won (excluidos)',
	contacts_reused: 'Contactos reutilizados',
	deals_reused: 'Negocios reutilizados',
	activities_reused: 'Actividades reutilizadas',
}

/**
 * @param {object} options
 * @returns {boolean}
 */
export function wantsCrmMigration(options) {
	if (!options || typeof options !== 'object') {
		return false
	}
	return Boolean(options.contacts || options.deals || options.activities)
}

export const STATUS_OPTIONS = [
	{ value: '', label: 'Todos los estados' },
	{ value: 'queued', label: 'En cola' },
	{ value: 'running', label: 'En ejecución' },
	{ value: 'completed', label: 'Completado' },
	{ value: 'partial', label: 'Parcial' },
	{ value: 'failed', label: 'Fallido' },
	{ value: 'cancelled', label: 'Cancelado' },
	{ value: 'rolled_back', label: 'Revertido' },
]

export const ITEM_STATUS_OPTIONS = [
	{ value: '', label: 'Todos los estados' },
	{ value: 'queued', label: 'En cola' },
	{ value: 'running', label: 'En ejecución' },
	{ value: 'success', label: 'Éxito' },
	{ value: 'skipped', label: 'Omitido' },
	{ value: 'error', label: 'Error' },
	{ value: 'cancelled', label: 'Cancelado' },
]

/**
 * Strict platform-admin gate: requires is_ae_admin && is_staff && is_active.
 * Missing flags fail closed and block API calls.
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function canAccessPropertyDuplication(user) {
	if (!user || typeof user !== 'object') {
		return false
	}

	return Boolean(user.is_ae_admin && user.is_staff && user.is_active)
}

/**
 * Destructive rollback of a migration job — requires Django superuser.
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function canRollbackPropertyDuplication(user) {
	return Boolean(canAccessPropertyDuplication(user) && user?.is_superuser)
}

/**
 * Parse UIDs from free text (newline / comma / space), trim and dedupe.
 * @param {string} raw
 * @returns {string[]}
 */
export function parsePropertyUids(raw) {
	if (!raw || typeof raw !== 'string') {
		return []
	}

	const parts = raw
		.split(/[\s,;]+/)
		.map((part) => part.trim())
		.filter(Boolean)

	const seen = new Set()
	const unique = []

	parts.forEach((uid) => {
		const key = uid.toLowerCase()
		if (!seen.has(key)) {
			seen.add(key)
			unique.push(uid)
		}
	})

	return unique
}

/**
 * Resolve overall job status from Import row (defensive).
 * @param {object|null|undefined} job
 * @returns {string}
 */
export function getJobOverallStatus(job) {
	if (!job || typeof job !== 'object') {
		return 'unknown'
	}

	const fromResult = job.result_data?.overall_status || job.result_data?.status || job.display_status
	if (fromResult) {
		return String(fromResult).toLowerCase()
	}

	if (job.status === '1' || job.status === 1) {
		return job.processed_rows > 0 ? 'running' : 'queued'
	}
	if (job.status === '2' || job.status === 2) {
		return 'completed'
	}
	if (job.status === '3' || job.status === 3) {
		return 'failed'
	}

	if (typeof job.status === 'string' && job.status) {
		return job.status.toLowerCase()
	}

	return 'unknown'
}

/**
 * @param {string} status
 * @returns {boolean}
 */
export function isActiveJobStatus(status) {
	return ACTIVE_STATUSES.includes(String(status || '').toLowerCase())
}

/**
 * @param {string} status
 * @returns {{ color: string, bg: string, label: string }}
 */
export function getStatusConfig(status) {
	const key = String(status || '').toLowerCase()
	const configs = {
		queued: { color: '#6b7280', bg: '#f3f4f6', label: 'En cola' },
		pending: { color: '#6b7280', bg: '#f3f4f6', label: 'Pendiente' },
		running: { color: '#2563eb', bg: '#eff6ff', label: 'En ejecución' },
		completed: { color: '#059669', bg: '#ecfdf5', label: 'Completado' },
		success: { color: '#059669', bg: '#ecfdf5', label: 'Éxito' },
		partial: { color: '#d97706', bg: '#fffbeb', label: 'Parcial' },
		failed: { color: '#dc2626', bg: '#fef2f2', label: 'Fallido' },
		error: { color: '#dc2626', bg: '#fef2f2', label: 'Error' },
		cancelled: { color: '#7c3aed', bg: '#f5f3ff', label: 'Cancelado' },
		rolled_back: { color: '#9f1239', bg: '#fff1f2', label: 'Revertido' },
		skipped: { color: '#6b7280', bg: '#f9fafb', label: 'Omitido' },
		unknown: { color: '#6b7280', bg: '#f3f4f6', label: 'Desconocido' },
	}

	return configs[key] || configs.unknown
}

/**
 * @param {string|null|undefined} dateString
 * @returns {string}
 */
export function formatDateTime(dateString) {
	if (!dateString) {
		return '—'
	}

	const date = new Date(dateString)
	if (Number.isNaN(date.getTime())) {
		return '—'
	}

	return date.toLocaleDateString('es-ES', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

/**
 * @param {number|null|undefined} ms
 * @returns {string}
 */
export function formatDurationMs(ms) {
	if (ms === null || ms === undefined || Number.isNaN(Number(ms))) {
		return '—'
	}

	const totalSeconds = Math.max(0, Math.round(Number(ms) / 1000))
	const hours = Math.floor(totalSeconds / 3600)
	const minutes = Math.floor((totalSeconds % 3600) / 60)
	const seconds = totalSeconds % 60

	if (hours > 0) {
		return `${hours}h ${minutes}m ${seconds}s`
	}
	if (minutes > 0) {
		return `${minutes}m ${seconds}s`
	}

	return `${seconds}s`
}

/**
 * Compute elapsed / ETA helpers from job fields.
 * @param {object|null|undefined} job
 * @returns {{ elapsedMs: number|null, etaMs: number|null, progressPct: number }}
 */
export function getJobProgressMeta(job) {
	if (!job || typeof job !== 'object') {
		return { elapsedMs: null, etaMs: null, progressPct: 0 }
	}

	const total = Number(job.total_rows) || 0
	const processed = Number(job.processed_rows) || 0
	let progressPct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0

	// Las props pueden ir al 100% mientras el CRM sigue; no mostrar 100% en esa fase.
	const stage = String(job.result_data?.stage || '')
	const status = String(job.result_data?.status || job.status || '')
	const isCrmRunning =
		status === 'running' && (stage === 'crm' || stage.startsWith('crm_')) && stage !== 'crm_done'
	if (isCrmRunning && progressPct >= 100) {
		progressPct = 95
	}

	const start = job.start_date ? new Date(job.start_date).getTime() : null
	const end = job.end_date ? new Date(job.end_date).getTime() : null
	const now = Date.now()

	let elapsedMs = null
	if (start && !Number.isNaN(start)) {
		elapsedMs = (end && !Number.isNaN(end) ? end : now) - start
	}

	const resultEta = job.result_data?.eta_ms ?? job.result_data?.eta_seconds
	let etaMs = null
	if (resultEta !== null && resultEta !== undefined) {
		etaMs = Number(job.result_data?.eta_ms !== undefined ? resultEta : Number(resultEta) * 1000)
	} else if (elapsedMs !== null && processed > 0 && total > processed) {
		const perItem = elapsedMs / processed
		etaMs = Math.round(perItem * (total - processed))
	}

	return { elapsedMs, etaMs, progressPct }
}

/**
 * Safe display name for a nested company object.
 * @param {object|string|null|undefined} company
 * @returns {string}
 */
export function getCompanyLabel(company) {
	if (!company) {
		return '—'
	}
	if (typeof company === 'string') {
		return company
	}

	return company.name || company.uid || company.id || '—'
}

/**
 * Safe display name for initiator / imported_by.
 * @param {object|string|null|undefined} user
 * @returns {string}
 */
export function getUserLabel(user) {
	if (!user) {
		return '—'
	}
	if (typeof user === 'string') {
		return user
	}

	const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
	return fullName || user.name || user.email || user.uid || '—'
}

/**
 * Extract job code from various possible locations.
 * @param {object|null|undefined} job
 * @returns {string}
 */
export function getJobCode(job) {
	if (!job || typeof job !== 'object') {
		return '—'
	}

	return (
		job.job_code ||
		job.result_data?.job_code ||
		job.data?.job_code ||
		job.imported_portal_key ||
		job.uid ||
		String(job.id || '—')
	)
}

/**
 * Extract options used for the job.
 * @param {object|null|undefined} job
 * @returns {object}
 */
export function getJobOptions(job) {
	if (!job || typeof job !== 'object') {
		return {}
	}

	return job.data?.options || job.options || job.result_data?.options || {}
}

/**
 * Extract entity counters from result_data.
 * @param {object|null|undefined} job
 * @returns {object}
 */
export function getJobCounters(job) {
	if (!job || typeof job !== 'object') {
		return {}
	}

	return job.result_data?.counts || job.result_data?.counters || {}
}

/**
 * Email completion state from result_data.
 * @param {object|null|undefined} job
 * @returns {{ state: 'sent'|'error'|'pending'|'n/a', detail: string }}
 */
export function getEmailState(job) {
	if (!job || typeof job !== 'object') {
		return { state: 'n/a', detail: '—' }
	}

	const rd = job.result_data || {}
	if (rd.completion_email_sent_at) {
		return { state: 'sent', detail: formatDateTime(rd.completion_email_sent_at) }
	}
	if (rd.completion_email_error) {
		return { state: 'error', detail: String(rd.completion_email_error) }
	}

	const status = getJobOverallStatus(job)
	if (isActiveJobStatus(status)) {
		return { state: 'pending', detail: 'Pendiente (job activo)' }
	}
	if (TERMINAL_STATUSES.includes(status)) {
		return { state: 'pending', detail: 'Pendiente de envío' }
	}

	return { state: 'n/a', detail: '—' }
}

/**
 * Extract API error message in Spanish-friendly form.
 * @param {any} err
 * @param {string} fallback
 * @returns {string}
 */
export function getApiErrorMessage(err, fallback = 'Ha ocurrido un error') {
	const data = err?.response?.data
	if (!data) {
		return err?.message || fallback
	}
	if (typeof data === 'string') {
		return data
	}
	if (data.error) {
		return typeof data.error === 'string' ? data.error : JSON.stringify(data.error)
	}
	if (data.detail) {
		return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail)
	}
	if (data.message) {
		return String(data.message)
	}

	const firstKey = Object.keys(data)[0]
	if (firstKey) {
		const value = data[firstKey]
		if (Array.isArray(value)) {
			return `${firstKey}: ${value.join(', ')}`
		}
		if (typeof value === 'string') {
			return `${firstKey}: ${value}`
		}
	}

	return fallback
}
